import type { components } from '@/api/qraft/schema';

type Language = components['schemas']['Language'];
export type TranslationStatus = components['schemas']['TranslationStatus'];

export type TranslationsTab = 'strings' | 'liteapp';

/**
 * The strings tab's `lang` is a backend `Language` (the language filter); the
 * LiteApp tab's `lang` is a LiteApp language code. Each tab narrows it itself,
 * so a value left over from the other tab reads as unset.
 */
export type TranslationsSearch = {
  tab?: TranslationsTab;
  unit?: number;
  lang?: string;
  domain?: string;
  context?: string;
  source?: string;
  translation?: string;
  status?: TranslationStatus;
  page?: number;
  screen?: string;
};

/** The three editable WPML languages; English is the source. */
export const STRING_LANGUAGES = [
  'ES',
  'FR',
  'PT_BR',
] as const satisfies readonly Language[];

export type StringLanguage = (typeof STRING_LANGUAGES)[number];

/** LiteApp language codes, paired with the backend enum for the label. */
export const LITEAPP_LANGUAGES = [
  ['en', 'EN'],
  ['es', 'ES'],
  ['fr', 'FR'],
  ['pt-br', 'PT_BR'],
] as const satisfies readonly (readonly [string, Language])[];

const STATUSES: readonly TranslationStatus[] = [
  'NOT_TRANSLATED',
  'NEEDS_UPDATE',
  'COMPLETE',
];

function text(raw: unknown): string | undefined {
  return typeof raw === 'string' && raw !== '' ? raw : undefined;
}

function whole(raw: unknown): number | undefined {
  const n =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string' && raw !== ''
        ? Number(raw)
        : Number.NaN;
  return Number.isInteger(n) && n >= 0 ? n : undefined;
}

export function validateTranslationsSearch(
  search: Record<string, unknown>,
): TranslationsSearch {
  const status = STATUSES.find((s) => s === search.status);
  return {
    tab: search.tab === 'liteapp' ? 'liteapp' : 'strings',
    unit: whole(search.unit),
    lang: text(search.lang),
    domain: text(search.domain),
    context: text(search.context),
    source: text(search.source),
    translation: text(search.translation),
    status,
    page: whole(search.page),
    screen: text(search.screen),
  };
}
